import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Share } from '@capacitor/share';
import { PushNotifications } from '@capacitor/push-notifications';

// Platform detection
export const isNativePlatform = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();

// Camera utilities
export const takePhoto = async (): Promise<string | null> => {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl
    });
    return image.dataUrl || null;
  } catch (error) {
    console.error('Error taking photo:', error);
    return null;
  }
};

export const pickImage = async (): Promise<string | null> => {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos
    });
    return image.dataUrl || null;
  } catch (error) {
    console.error('Error picking image:', error);
    return null;
  }
};

// Geolocation utilities
export const getCurrentPosition = async () => {
  try {
    const coordinates = await Geolocation.getCurrentPosition();
    return {
      latitude: coordinates.coords.latitude,
      longitude: coordinates.coords.longitude,
      accuracy: coordinates.coords.accuracy
    };
  } catch (error) {
    console.error('Error getting location:', error);
    throw error;
  }
};

// Haptic feedback
export const hapticImpact = async (style: 'light' | 'medium' | 'heavy' = 'medium') => {
  try {
    await Haptics.impact({ 
      style: style === 'light' ? ImpactStyle.Light : 
             style === 'heavy' ? ImpactStyle.Heavy : 
             ImpactStyle.Medium 
    });
  } catch (error) {
    console.error('Haptic feedback not available:', error);
  }
};

export const hapticNotification = async (type: 'success' | 'warning' | 'error' = 'success') => {
  try {
    const notificationType = type === 'success' ? NotificationType.Success : 
                           type === 'warning' ? NotificationType.Warning : 
                           NotificationType.Error;
    await Haptics.notification({ type: notificationType });
  } catch (error) {
    console.error('Haptic notification not available:', error);
  }
};

// Share functionality
export const shareContent = async (title: string, text: string, url?: string) => {
  try {
    await Share.share({
      title,
      text,
      url,
      dialogTitle: 'Share via'
    });
  } catch (error) {
    console.error('Error sharing:', error);
  }
};

// Push notifications
export const initializePushNotifications = async () => {
  try {
    // Request permission to use push notifications
    await PushNotifications.requestPermissions();

    // Register with Apple / Google to receive push via APNS/FCM
    await PushNotifications.register();

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Error on registration: ', error);
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener('pushNotificationReceived', (notification: any) => {
      console.log('Push notification received: ', notification);
    });

    // Method called when tapping on a notification
    PushNotifications.addListener('pushNotificationActionPerformed', (notification: any) => {
      console.log('Push notification action performed', notification.actionId, notification.inputValue);
    });

    return true;
  } catch (error) {
    console.error('Push notification setup failed:', error);
    return false;
  }
};